import { mcpServerListResponseSchema, mcpServerUsersResponseSchema, listMcpServersParamsSchema, organizationMcpListParamsSchema, mcpServerNameSchema, mcpFilterOptionsQuerySchema, mcpFilterOptionsResponseSchema, mcpTableResponseSchema, mcpServerPolicyToggleBodySchema, mcpServerPolicyToggleResponseSchema, mcpServerGroupPolicyResetResponseSchema, mcpServerGroupPoliciesSummarySchema, mcpServerGroupPoliciesResetResponseSchema, mcpServerRiskDetailSchema, mcpServerRiskQuerySchema, } from '../contracts/mcp';
import { listPersonalMcpServersDataTableParamsSchema, personalMcpFilterOptionsQuerySchema, personalMcpFilterOptionsResponseSchema, personalMcpTableResponseSchema, } from '../contracts/account-mcp';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
/**
 * List MCP servers for an organization
 *
 * Authorization:
 * - Owner/Manager: Returns all MCP servers in the organization
 * - Member: Returns only the user's own MCP servers
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of MCP servers
 */
export async function listOrganizationMCPServers(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(organizationMcpListParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/mcp-servers`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(mcpTableResponseSchema, response);
}
/**
 * Allow or block an Inventory MCP server for the whole organization
 * (KIR-4482). The endpoint-server counterpart of the gateway's
 * `PUT .../gateway/servers/:id/policy`.
 *
 * The decision applies to the servers behind the row (keyed by their resolved
 * identity), not to its name, and is delivered to endpoints and enforced by
 * the endpoint hook. Either decision marks the row reviewed, clearing New.
 *
 * Authorization: Owner/Manager.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param name - The Inventory row (`MCPServerListItem.name`)
 * @param body - The decision
 * @returns The stored decision
 */
export async function updateOrganizationMCPServerPolicy(client, organizationId, name, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(mcpServerPolicyToggleBodySchema, body);
    const response = await client.put(`/organizations/${validatedId}/mcp-servers/${encodeURIComponent(name)}/policy`, validatedBody);
    return parseSchema(mcpServerPolicyToggleResponseSchema, response);
}
/**
 * Allow or block an Inventory MCP server for one security group (KIR-4701).
 *
 * Authorization: Owner/Manager.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param securityGroupId - The security group
 * @param name - The Inventory row (`MCPServerListItem.name`)
 * @param body - The decision
 * @returns The stored decision
 */
export async function updateGroupMCPServerPolicy(client, organizationId, securityGroupId, name, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(mcpServerPolicyToggleBodySchema, body);
    const response = await client.put(`/organizations/${validatedId}/security-groups/${encodeURIComponent(securityGroupId)}/mcp-servers/${encodeURIComponent(name)}/policy`, validatedBody);
    return parseSchema(mcpServerPolicyToggleResponseSchema, response);
}
/**
 * Drop a security group's MCP choice for one server (KIR-4701). A group with
 * other choices takes the org's current choice for it; otherwise the group
 * inherits the org's again.
 *
 * Authorization: Owner/Manager.
 */
export async function deleteGroupMCPServerPolicy(client, organizationId, securityGroupId, name) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.delete(`/organizations/${validatedId}/security-groups/${encodeURIComponent(securityGroupId)}/mcp-servers/${encodeURIComponent(name)}/policy`);
    return parseSchema(mcpServerGroupPolicyResetResponseSchema, response);
}
/**
 * How many MCP servers a security group has its own Allow/Block for (KIR-4701).
 *
 * Authorization: Owner/Manager/Support.
 */
export async function getGroupMCPServerPoliciesSummary(client, organizationId, securityGroupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/${encodeURIComponent(securityGroupId)}/mcp-servers/policies`);
    return parseSchema(mcpServerGroupPoliciesSummarySchema, response);
}
/**
 * Drop every MCP choice of a security group so its members inherit the org's
 * (KIR-4701).
 *
 * Authorization: Owner/Manager.
 */
export async function resetGroupMCPServerPolicies(client, organizationId, securityGroupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.delete(`/organizations/${validatedId}/security-groups/${encodeURIComponent(securityGroupId)}/mcp-servers/policies`);
    return parseSchema(mcpServerGroupPoliciesResetResponseSchema, response);
}
/**
 * List personal MCP servers (for users without an organization)
 *
 * Returns MCP servers where organizationId is NULL, filtered by the authenticated user.
 * Uses the legacy response shape ({ servers, pagination }).
 *
 * @param client - Axios client instance
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of personal MCP servers (legacy shape)
 */
export async function listUserMCPServers(client, params = {}) {
    const validatedParams = parseSchema(listMcpServersParamsSchema, params);
    const response = await client.get('/account/mcp-servers', {
        params: validatedParams,
    });
    return parseSchema(mcpServerListResponseSchema, response);
}
/**
 * Get users who have a specific MCP server configured
 *
 * Authorization:
 * - Owner/Manager: Returns all users with this MCP
 * - Member: Returns only the requesting user's data
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param serverName - Row identity: the `.mcp.json` `config_name`. Param
 *                     kept for caller back-compat; sent as `configName`.
 * @returns List of users with this MCP configured
 */
export async function getMCPServerUsers(client, organizationId, serverName) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedConfigName = parseSchema(mcpServerNameSchema, serverName);
    const response = await client.post(`/organizations/${validatedId}/mcp-servers/users`, { configName: validatedConfigName });
    return parseSchema(mcpServerUsersResponseSchema, response);
}
/**
 * AgentMesh risk detail for one MCP row (KIR-5108): the row's value (its
 * worst installed version), that version's top risk factors and report link,
 * and every installed version. 404 while `agentmesh_mcp_risk` is off.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param name - Row identity: the `.mcp.json` `config_name`
 * @param options - Optional AbortSignal for request cancellation
 */
export async function getMCPServerRisk(client, organizationId, name, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedQuery = parseSchema(mcpServerRiskQuerySchema, { name });
    const response = await client.get(`/organizations/${validatedId}/mcp-servers/risk`, { params: validatedQuery, signal: options?.signal });
    return parseSchema(mcpServerRiskDetailSchema, response);
}
/**
 * List personal MCP servers with DataTable support (filters, search, sorting).
 *
 * Sends filters using bracket notation via serializeTableQueryParams.
 * The response follows the standard DataTable shape: { data, pagination, meta }.
 *
 * @param client - Axios client instance
 * @param params - DataTable query parameters (pagination, filters, sort, search)
 * @param options - Optional AbortSignal for request cancellation
 * @returns DataTable response with MCP server rows
 */
export async function listUserMCPServersDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalMcpServersDataTableParamsSchema, params);
    const response = await client.get('/account/mcp-servers/table', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalMcpTableResponseSchema, response);
}
/**
 * Get filter option values for a specific MCP field (organization level).
 * Supports name, version, and publisher fields (dynamic options).
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - Field, search, limit, offset
 * @returns Paginated list of option strings
 */
export async function getMCPFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(mcpFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/mcp-servers/filter-options`, { params: validatedParams });
    return parseSchema(mcpFilterOptionsResponseSchema, response);
}
/**
 * Get distinct filter option values for a personal MCP servers field.
 *
 * @param client - Axios client instance
 * @param params - Field, optional search, limit, offset
 * @returns List of option values and pagination info
 */
export async function getUserMCPFilterOptions(client, params) {
    const validatedParams = parseSchema(personalMcpFilterOptionsQuerySchema, params);
    const response = await client.get('/account/mcp-servers/filter-options', {
        params: validatedParams,
    });
    return parseSchema(personalMcpFilterOptionsResponseSchema, response);
}
//# sourceMappingURL=mcp.js.map